import { Calendar, Clock, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { date } from "@/lib/date";
import { daysToClose, deadlineLabel, elapsedPercent, isOrdersOpen } from "@/lib/sale";
import { orderCode, pad2 } from "@/lib/format";
import type { SaleWithRelations } from "@/types/sale-with-relations";
import { Label, Numeral, ProgressBar, StatusPill, Surface, Display } from "./primitives";
import { QtyStepper } from "./controls";

export function CampaignHero({
  sale,
  className,
}: {
  sale: Pick<
    SaleWithRelations,
    "name" | "description" | "startDate" | "endDate" | "ordersClosedAt" | "ordersManual"
  >;
  className?: string;
}) {
  const open = isOrdersOpen(sale);
  const days = daysToClose(sale);

  return (
    <Surface hero focus className={cn("flex flex-col gap-4", className)}>
      <div className="flex items-center justify-between gap-3">
        <Label accent>Campanha</Label>
        <StatusPill
          open={open}
          label={open ? "PEDIDOS ABERTOS" : "PEDIDOS FECHADOS"}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Display size="h1" as="h2" className="md:text-4xl md:leading-[34px] md:tracking-[-0.033em]">
          {sale.name}
        </Display>
        {sale.description && (
          <p className="text-sm leading-5 text-fx-secondary">{sale.description}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[13px] font-medium text-fx-secondary [&_svg]:text-fx-red-icon">
        <span className="flex items-center gap-1.5">
          <Calendar size={14} strokeWidth={1.6} />
          Entrega {date(sale.endDate).format("DD/MM")}
        </span>
        <span className="flex items-center gap-1.5">
          <Clock size={14} strokeWidth={1.6} />
          {deadlineLabel(sale)}
        </span>
      </div>

      {open && !sale.ordersManual && (
        <div className="mt-auto flex flex-col gap-3.5 pt-1">
          <div className="flex items-end gap-3">
            <Numeral size="hero" cream>
              {pad2(days)}
            </Numeral>
            <div className="flex flex-col gap-[3px] pb-[3px]">
              <Label>Dias para</Label>
              <Label>Fechar pedidos</Label>
            </div>
          </div>
          <ProgressBar value={elapsedPercent(sale)} />
        </div>
      )}
    </Surface>
  );
}

export function FlavorCard({
  name,
  description,
  quantity,
  onChange,
  obs,
  onObsChange,
  className,
}: {
  name: string;
  description?: string;
  quantity: number;
  onChange: (quantity: number) => void;
  /** Observação deste sabor; o campo aparece quando a quantidade é maior que zero. */
  obs: string;
  onObsChange: (obs: string) => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2.5 rounded-card border bg-fx-surface py-2.5 pr-2.5 pl-4 transition-[border-color] duration-[180ms] ease-fx md:p-4",
        quantity > 0 ? "border-fx-red-line" : "border-fx-line-subtle",
        className,
      )}
    >
      <div className="flex items-center gap-3 md:flex-col md:items-stretch">
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <span className="text-[14.5px] font-semibold text-fx-title">{name}</span>
          {description && (
            <span className="line-clamp-1 text-xs text-fx-faint md:line-clamp-2 md:min-h-[34px] md:text-[12.5px] md:leading-[17px]">
              {description}
            </span>
          )}
        </div>
        <QtyStepper
          value={quantity}
          onChange={onChange}
          label={name}
          className="w-[132px] shrink-0 md:w-full"
        />
      </div>
      {quantity > 0 && (
        <input
          type="text"
          value={obs}
          onChange={(e) => onObsChange(e.target.value)}
          maxLength={120}
          placeholder="Observação (opcional)"
          aria-label={`Observação de ${name}`}
          className="mr-1.5 h-10 rounded-md border border-fx-line bg-fx-input px-3 text-[13px] text-fx-title outline-none transition-[border-color,box-shadow] duration-[180ms] ease-fx placeholder:text-fx-faint focus:border-fx-red-line focus:ring-[3px] focus:ring-fx-focus-ring md:mr-0"
        />
      )}
    </div>
  );
}

type OrderCardOrder = SaleWithRelations["orders"][number];

export function OrderCard({
  order,
  position,
  seller,
  canEdit,
  onEdit,
  onRemove,
}: {
  order: OrderCardOrder;
  position: number;
  /** Nome do vendedor, quando a lista mistura pedidos de várias pessoas. */
  seller?: string;
  canEdit?: boolean;
  onEdit?: () => void;
  onRemove?: () => void;
}) {
  const created = date(order.createdAt as unknown as string);
  const today = date().startOf("date");
  const when = created.isSame(today, "day")
    ? `hoje, ${created.format("HH:mm")}`
    : created.isSame(today.subtract(1, "day"), "day")
      ? `ontem, ${created.format("HH:mm")}`
      : created.format("DD/MM, HH:mm");

  return (
    <Surface className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-3">
        <Display size="h5" as="h3">
          Pedido {orderCode(position)}
        </Display>
        <span className="flex-1 text-xs text-fx-faint">
          {seller ? `${seller} · ${when}` : when}
        </span>
        {canEdit && (
          <button
            type="button"
            onClick={onEdit}
            aria-label={`Editar pedido ${orderCode(position)}`}
            className="-my-2.5 flex size-11 items-center justify-center rounded-md text-fx-body transition-colors hover:bg-fx-raised"
          >
            <Pencil size={17} strokeWidth={1.6} />
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remover pedido ${orderCode(position)}`}
            className="-m-2.5 flex size-11 items-center justify-center rounded-md text-fx-danger-ink transition-colors hover:bg-fx-red-surface"
          >
            <Trash2 size={18} strokeWidth={1.6} />
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {order.items.map((item) => {
          const obs = item.obs || order.obs;
          return (
            <div
              key={item.id}
              className={cn(
                "flex flex-col gap-0.5 border border-fx-line px-3 py-1.5",
                obs ? "rounded-lg" : "rounded-full",
              )}
            >
              <span className="flex items-baseline gap-1.5">
                <span className="fx-num text-[15px] text-fx-cream">{item.quantity}×</span>
                <span className="text-[13px] text-fx-body">{item.flavor?.name}</span>
              </span>
              {obs && (
                <span className="text-xs italic leading-4 text-fx-secondary">"{obs}"</span>
              )}
            </div>
          );
        })}
      </div>
    </Surface>
  );
}

/** Linha de lista com trilho vermelho: usada nos "Meus pedidos" do Início. */
export function GroupRow({
  title,
  meta,
  value,
  to,
}: {
  title: string;
  meta: string;
  value: number;
  to?: string;
}) {
  return (
    <div className="flex min-h-14 items-center gap-3 border-b border-fx-line-subtle px-[18px] py-2.5 last:border-b-0">
      <span className="h-[26px] w-[3px] shrink-0 rounded-full bg-fx-red-icon" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 md:flex-row md:items-center md:gap-3">
        <span className="truncate text-sm font-semibold text-fx-title md:flex-1">{title}</span>
        <span className="text-xs text-fx-faint md:w-40 md:text-[12.5px]">{meta}</span>
      </div>
      <Numeral size="xs" cream className="w-7 text-right">
        {value}
      </Numeral>
    </div>
  );
}
